import React from 'react';
import { Box, LinearProgress, Paper, Typography } from '@mui/material';
import { GetInterventionCostsResponse, InterventionLadderStepModel } from '../../../api-service/clients';
import { CircularProgressCentered } from '../../../components/circular-progress-centered';
import { formatActivitiesWithoutPrice } from '../intervention-ladder/intervention-cost';
import { externalCostColor, internalCostColor } from '../intervention-ladder/intervention-ladder-colors';
import { InterventionCostChart } from './intervention-cost-chart';

export interface InterventionCostsComponentProps {
  step: InterventionLadderStepModel;
  costs: GetInterventionCostsResponse | undefined;
  isLoading: boolean;
  hasFailed: boolean;
}

export function InterventionCostsComponent({ step, costs, isLoading, hasFailed }: InterventionCostsComponentProps) {
  const years = costs !== undefined && costs.years.length > 0 ? ` (${costs.years[0]}–${costs.years[costs.years.length - 1]})` : '';
  return (
    <Paper sx={{ p: 2, mt: 2, position: 'relative', overflow: 'hidden' }} aria-busy={isLoading}>
      {isLoading && costs !== undefined && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3 }} />}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
        <Box>
          <Typography variant="h2" sx={{ fontSize: '1rem', mb: 0.5 }}>
            Udgifter
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', fontStyle: 'italic', color: 'text.secondary' }}>
            Udgifter pr. paragraf på{' '}
            <Box component="strong" sx={{ fontStyle: 'normal', fontWeight: 600, color: 'text.primary' }}>
              Trin {step.step} · {step.title}
            </Box>{' '}
            · fordelt på interne og eksterne · grupperet pr. år{years} · mio. kr.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, whiteSpace: 'nowrap', fontSize: '0.8125rem', color: 'text.secondary' }}>
          Valgt trin
          <Box component="strong" sx={{ fontSize: '0.9375rem', fontWeight: 600, color: '#16323d' }}>
            Trin {step.step}
          </Box>
        </Box>
      </Box>
      <InterventionCostsContent costs={costs} isLoading={isLoading} hasFailed={hasFailed} />
    </Paper>
  );
}

interface InterventionCostsContentProps {
  costs: GetInterventionCostsResponse | undefined;
  isLoading: boolean;
  hasFailed: boolean;
}

function InterventionCostsContent({ costs, isLoading, hasFailed }: InterventionCostsContentProps) {
  if (hasFailed) {
    return <Typography>Udgifterne kunne ikke hentes.</Typography>;
  }
  if (costs === undefined) {
    return <CircularProgressCentered />;
  }
  if (costs.paragraphs.length === 0) {
    return <Typography>Trinnet har ingen paragraffer.</Typography>;
  }
  const yearsWithoutPrice = costs.totals.filter(year => year.activitiesWithoutPrice > 0);
  return (
    <Box sx={{ opacity: isLoading ? 0.5 : 1, transition: 'opacity 150ms' }}>
      <CostLegend />
      {costs.totals.some(year => year.total > 0) ? (
        <InterventionCostChart
          labels={costs.paragraphs.map(paragraph => paragraph.paragraph)}
          years={costs.years.map((year, index) => ({
            year,
            internal: costs.paragraphs.map(paragraph => paragraph.years[index].internal),
            external: costs.paragraphs.map(paragraph => paragraph.years[index].external),
          }))}
        />
      ) : (
        <Box sx={{ height: 340, display: 'grid', placeItems: 'center', color: 'text.secondary', fontSize: '0.875rem' }}>Ingen udgifter</Box>
      )}
      {yearsWithoutPrice.length > 0 && (
        <Typography sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
          Udgifterne er for lave, da der er {yearsWithoutPrice.map(year => `${formatActivitiesWithoutPrice(year.activitiesWithoutPrice)} i ${year.year}`).join(', ')}.
        </Typography>
      )}
    </Box>
  );
}

function CostLegend() {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1, fontSize: '0.75rem', color: '#5b6b73' }}>
      <LegendSwatch color={internalCostColor} label="Interne udgifter" />
      <LegendSwatch color={externalCostColor} label="Eksterne udgifter" />
      <span>Søjle = samlet udgift · stablet på interne + eksterne · én søjle pr. år</span>
    </Box>
  );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box component="span" sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color }} />
      {label}
    </Box>
  );
}
